import type { CatalogOf } from '../types';
import type { en } from './en';

export const ms: CatalogOf<typeof en> = {
  common: {
    save: 'Simpan',
    cancel: 'Batal',
    apply: 'Guna',
    close: 'Tutup',
    back: 'Kembali',
    verify: 'Sahkan',
    loading: 'Memuatkan...',
    retry: 'Cuba lagi',
    or: 'atau',
    today: 'Hari ini',
    doctor: 'Doktor',
    clinic: 'Klinik',
    unknown: 'Tidak diketahui',
    copy: 'Salin',
    copied: 'Disalin!',
    add: 'Tambah',
  },
  language: {
    label: 'Bahasa antara muka',
    english: 'English',
    indonesian: 'Bahasa Indonesia',
  },
  header: {
    sessions: 'Sesi',
    back: 'Kembali ke sesi',
    logout: 'Log keluar',
    history: 'Sejarah',
    chatWithEhr: 'Sembang dengan EHR',
    dashboard: 'Papan pemuka',
    clinic: 'Klinik',
    aiUsage: 'Penggunaan AI',
    profile: 'Profil',
    endSession: 'Tamatkan sesi',
    stopViewing: 'Berhenti melihat',
    actingAs: 'Bertindak sebagai {name} · {clinic}',
    impersonation: '(Penyamaran)',
    viewingClinic: 'Melihat: {clinic}',
    readOnly: '(Baca sahaja)',
    logoAlt: 'Logo AIGP Health',
    pages: {
      consultTitle: 'Konsultasi Pesakit',
      consultSubtitle: 'Skrib AI dan dokumentasi klinikal',
      dashboardTitle: 'Papan pemuka doktor',
      dashboardSubtitle: 'Selamat kembali, {name}',
      sessionsTitle: 'Urus sesi',
      sessionsSubtitle: 'Cipta dan urus sesi konsultasi pesakit',
      meetingTitle: 'Konsultasi dalam talian',
      meetingSubtitle: 'Bilik mesyuarat maya untuk konsultasi pesakit',
      profileTitle: 'Profil saya',
      profileSubtitle: 'Urus maklumat akaun dan keutamaan anda',
      clinicProfileTitle: 'Profil klinik',
      clinicProfileSubtitle: 'Urus maklumat dan tetapan klinik',
      clinicAdminTitle: 'Pentadbiran klinik',
      clinicAdminSubtitle: 'Selamat datang, {name}',
      adminTitle: 'Papan pemuka pentadbir',
      adminSubtitle: 'Selamat kembali, {name}',
      superAdminTitle: 'Papan pemuka pentadbir atasan',
      superAdminSubtitle: 'Selamat datang, {name}',
      aiUsageTitle: 'Penggunaan AI',
      aiUsageSubtitle: 'Penggunaan token, kos dan latensi merentas aliran AI',
      defaultTitle: 'AIGP Health',
      defaultSubtitle: 'Platform konsultasi perubatan',
      adminFallback: 'Pentadbir',
      superAdminFallback: 'Pentadbir atasan',
    },
  },
  auth: {
    welcomeBack: 'Selamat kembali',
    loginSubtitle: 'Log masuk untuk meneruskan ke ruang kerja AIGP Health anda.',
    signInEyebrow: 'Log masuk',
    signInTitle: 'Log masuk ke akaun anda',
    email: 'Alamat e-mel',
    emailPlaceholder: 'you@clinic.com',
    password: 'Kata laluan',
    passwordPlaceholder: 'Masukkan kata laluan anda',
    showPassword: 'Tunjuk kata laluan',
    hidePassword: 'Sembunyi kata laluan',
    signIn: 'Log masuk',
    signInGoogle: 'Log masuk dengan Google',
    forgotPassword: 'Lupa kata laluan?',
    createAccount: 'Cipta akaun',
    verifyEyebrow: 'Sahkan identiti',
    enterCode: 'Masukkan kod anda',
    otpHint: 'Masukkan kod 6 digit yang dihantar ke telefon berdaftar anda melalui WhatsApp.',
    loginOtpAria: 'Kod pengesahan log masuk',
    resendIn: 'Hantar semula kod dalam {seconds}s',
    requestNewCode: 'Minta kod baharu',
    secureEyebrow: 'Lindungi akaun anda',
    mfaTitle: 'Sediakan pengesahan dua faktor',
    mfaHint: 'Tambah nombor telefon untuk menerima kod sekali guna melalui WhatsApp.',
    phone: 'Nombor telefon',
    phonePlaceholder: 'Masukkan nombor telefon',
    sendCode: 'Hantar kod',
    resendCode: 'Hantar semula kod',
    mfaOtpAria: 'Kod pengesahan persediaan MFA',
    invalidEmail: 'Sila masukkan alamat e-mel yang sah.',
    enterPassword: 'Sila masukkan kata laluan anda.',
    emailUnverified: 'E-mel anda belum disahkan. Sahkan dahulu sebelum menyediakan MFA.',
    authError: 'Ralat pengesahan. Sila hubungi sokongan.',
    badCredentials: 'E-mel atau kata laluan tidak betul. Sila cuba lagi.',
    unableToSignIn: 'Tidak dapat log masuk. Sila cuba lagi.',
    enterFullCode: 'Sila masukkan kod 6 digit yang lengkap.',
    sessionExpired: 'Sesi tamat. Sila log masuk semula.',
    invalidCode: 'Kod tidak sah. Sila cuba lagi.',
    couldNotResend: 'Tidak dapat menghantar semula kod.',
    startOver: 'Mula semula',
    attemptRemaining: '{count} percubaan tinggal',
    attemptsRemaining: '{count} percubaan tinggal',
    invalidPhone: 'Sila masukkan nombor telefon yang sah.',
    couldNotSend: 'Tidak dapat menghantar kod.',
    googleUnexpected: 'Respons tidak dijangka daripada log masuk Google.',
    googleFailed: 'Log masuk Google gagal.',
    routingFailed: 'Log masuk berjaya tetapi penghalaan gagal. Sila cuba lagi.',
    shellDefaultTitle: 'Selamat datang ke AIGP Health',
    shellDefaultSubtitle: 'Sokongan keputusan klinikal berasaskan bukti untuk reumatologi dan amalan am.',
    trustHipaa: 'Pengendalian data selaras HIPAA',
    trustEncrypted: 'Disulitkan semasa transit dan storan',
    trustEvidence: 'Bukti disahkan oleh klinisi',
    registrationPhases: 'Fasa pendaftaran',
    trustAria: 'Kepercayaan dan keselamatan',
    progress: 'Kemajuan',
    disclaimerTitle: 'Penafian',
    disclaimerBody:
      'AIGP Health dan platform AI-nya "Anzu" bertujuan membantu — bukan menggantikan — pertimbangan klinikal profesional kesihatan yang berkelayakan. Semua keputusan klinikal mesti dibuat oleh pengamal perubatan berlesen. Maklumat daripada Anzu bukan pengganti nasihat, diagnosis atau rawatan perubatan profesional.',
    iUnderstand: 'Saya faham',
  },
  forgot: {
    title: 'Tetapkan semula kata laluan',
    subtitle: 'Kami akan membantu anda kembali ke akaun anda.',
    eyebrow: 'Pemulihan akaun',
    stepEmail: 'Tetapkan semula kata laluan',
    stepOtp: 'Sahkan kod',
    stepReset: 'Tetapkan kata laluan baharu',
    hintEmail: 'Masukkan e-mel anda dan kami akan menghantar kod tetapan semula.',
    hintOtp: 'Masukkan kod 6 digit yang dihantar ke telefon anda.',
    hintReset: 'Masukkan token daripada e-mel anda dan pilih kata laluan baharu.',
    sendResetCode: 'Hantar kod tetapan semula',
    otpAria: 'Kod tetapan semula kata laluan',
    resetToken: 'Token tetapan semula',
    tokenPlaceholder: 'Tampal token daripada e-mel anda',
    newPassword: 'Kata laluan baharu',
    newPasswordPlaceholder: 'Masukkan kata laluan baharu',
    confirmPassword: 'Sahkan kata laluan baharu',
    confirmPlaceholder: 'Masukkan semula kata laluan baharu',
    resetPassword: 'Tetapkan semula kata laluan',
    backToSignIn: 'Kembali ke log masuk',
    codeSent: 'Kod tetapan semula telah dihantar ke telefon anda.',
    codeResent: 'Kod tetapan semula baharu telah dihantar.',
    sendFailed: 'Gagal menghantar kod. Sila cuba lagi.',
    resendFailed: 'Gagal menghantar semula kod.',
    enterFullCode: 'Sila masukkan kod 6 digit yang lengkap.',
    tokenSent: 'Token tetapan semula dihantar ke e-mel anda. Masukkannya di bawah bersama kata laluan baharu.',
    invalidCode: 'Kod tidak sah. Sila cuba lagi.',
    enterToken: 'Sila masukkan token tetapan semula daripada e-mel anda.',
    passwordRequirements: 'Kata laluan tidak memenuhi keperluan.',
    passwordsMismatch: 'Kata laluan tidak sepadan.',
    resetSuccess: 'Kata laluan berjaya ditetapkan semula. Menghala ke log masuk…',
    resetFailed: 'Gagal menetapkan semula kata laluan. Sila cuba lagi.',
  },
  dashboard: {
    approvalTitle: 'Kelulusan klinik tertunda',
    approvalBody: 'Pendaftaran klinik anda sedang disemak oleh pasukan pentadbiran kami.',
    approvalAccess: 'Anda boleh mengakses portal doktor selepas klinik anda diluluskan.',
    approvalHelp: 'Hubungi pentadbir klinik atau sokongan jika anda ada soalan.',
    welcome: 'Selamat sejahtera, Dr. {name}!',
    welcomeBody: 'Bersedia mengurus sesi pesakit dan memberikan rawatan terbaik',
    whatToDo: 'Apa yang ingin anda lakukan?',
    manageSessions: 'Urus sesi',
    manageSessionsBody: 'Cipta, sunting dan susun sesi konsultasi pesakit',
    manageFeature1: 'Jadualkan konsultasi baharu',
    manageFeature2: 'Kemas kini maklumat pesakit',
    manageFeature3: 'Urus butiran temujanji',
    clinicAdmin: 'Pentadbiran klinik',
    clinicAdminBody: 'Urus pengguna, langganan dan ringkasan klinik',
    clinicFeature1: 'Gambaran keseluruhan & analitik',
    clinicFeature2: 'Pengurusan pengguna',
    clinicFeature3: 'Pengurusan langganan',
  },
  usage: {
    loadingChip: 'Sesi…',
    loadingBanner: 'Memuatkan penggunaan sesi anda…',
    loadingTitle: 'Memuatkan penggunaan sesi',
    errorChip: 'Sesi —',
    errorBanner: 'Tidak dapat memuatkan penggunaan sesi anda. {error}',
    loadFailed: 'Gagal memuatkan penggunaan sesi',
    unlimitedTitle: 'Sesi tanpa had — tiada had bulanan',
    usedTitle: '{used} daripada {limit} sesi digunakan bulan ini',
    unlimitedChip: 'Sesi ∞',
    sessionsLabel: 'sesi',
    unlimitedBanner: 'Tanpa had — tiada had bulanan.',
    usedThisPeriod: 'Anda telah menggunakan {used} sesi dalam tempoh ini.',
    usedThisPeriodPlural: 'Anda telah menggunakan {used} sesi dalam tempoh ini.',
    reachedLimit: 'Anda telah mencapai had sesi bulanan ({used}/{limit}). Sesi ditetapkan semula pada tempoh seterusnya.',
    oneLeft: '1 sesi tinggal bulan ini. {used}/{limit} digunakan.',
    manyLeft: '{remaining} sesi tinggal bulan ini. {used}/{limit} digunakan.',
  },
  inactivity: {
    title: 'Sesi akan tamat tidak lama lagi',
    body: 'Sesi anda akan tamat kerana tidak aktif dalam:',
    continueQuestion: 'Adakah anda ingin meneruskan sesi?',
    logoutNow: 'Log keluar sekarang',
    continueSession: 'Teruskan sesi',
  },
  subscription: {
    title: 'Langganan tertunda',
    bodyNamed: 'Dr. {name}, akaun anda sudah sedia, tetapi klinik anda belum mempunyai langganan aktif.',
    bodyUnnamed: 'Akaun anda sudah sedia, tetapi klinik anda belum mempunyai langganan aktif.',
    access: 'Anda boleh mengakses portal doktor sebaik klinik anda berada pada pelan.',
    help: 'Hanya pentadbir klinik boleh mengaktifkan langganan. Selepas itu, semak semula di bawah atau log masuk semula.',
    checking: 'Menyemak…',
    checkAgain: 'Semak semula',
    stillPending: 'Klinik anda masih belum mempunyai langganan aktif. Sila semak dengan pentadbir klinik.',
    checkFailed: 'Tidak dapat menyemak status langganan. Sila cuba sebentar lagi.',
  },
  sessions: {
    tabSessions: 'Sesi',
    tabSignature: 'Tandatangan',
    tabMedicationCatalog: 'Katalog ubat',
    tabProtocols: 'Protokol',
    tabEducation: 'Kandungan pendidikan',
    tabScheduling: 'Penjadualan',
    noSignature: 'Tiada tandatangan ditetapkan',
    filterToday: 'Hari ini',
    filterTomorrow: 'Esok',
    filterThisWeek: 'Minggu ini',
    filterAll: 'Semua',
    filterCustom: 'Julat tersuai',
    listView: 'Paparan senarai',
    calendarView: 'Paparan kalendar',
    allDoctors: 'Semua doktor',
    sortBy: 'Isih:',
    sortDefault: 'Lalai (terbaharu dahulu)',
    sortDateNewest: 'Tarikh temujanji — terbaharu dahulu',
    sortDateOldest: 'Tarikh temujanji — terlama dahulu',
    sortTimeEarliest: 'Masa temujanji — paling awal',
    sortTimeLatest: 'Masa temujanji — paling lewat',
    sortIdLow: 'ID sesi — rendah ke tinggi',
    sortIdHigh: 'ID sesi — tinggi ke rendah',
    searchPlaceholder: 'Cari nama atau telefon pesakit...',
    bulkUpload: 'Muat naik pukal',
    createNew: 'Cipta sesi baharu',
    loading: 'Memuatkan sesi...',
    appointmentsHeading: 'Temujanji {filter}',
    appointmentsRange: 'Temujanji {start} - {end}',
    emptyTitle: 'Tiada temujanji {filter} dijumpai',
    emptySearch: 'Tiada temujanji {filter} sepadan dengan carian anda.',
    emptyScheduled: 'Tiada temujanji dijadualkan untuk {filter}.',
    emptyRange: 'Tiada temujanji dijumpai untuk {start} - {end}',
    emptyRangeSearch: 'Tiada temujanji dalam julat tarikh dipilih yang sepadan dengan carian anda.',
    emptyAll: 'Tiada sesi. Cipta sesi pertama anda untuk bermula.',
    emptyRangeScheduled: 'Tiada temujanji dijadualkan untuk julat tarikh dipilih.',
    emptyPickRange: 'Sila pilih julat tarikh untuk melihat temujanji.',
    showingRange: 'Menunjukkan {start}-{end} daripada {total}',
    colPatient: 'Nama pesakit',
    colPatientHint: 'Guna carian untuk mencari pesakit',
    colPhone: 'Nombor telefon',
    colDoctor: 'Doktor',
    colNotification: 'Pemberitahuan',
    colPreconsult: 'Pra-konsultasi',
    colType: 'Jenis sesi',
    sent: 'Dihantar',
    notSent: 'Belum dihantar',
    rowsPerPage: 'Baris setiap halaman:',
    statusNotArrived: 'Belum tiba',
    statusCheckedIn: 'Telah daftar masuk',
    statusInConsultation: 'Dalam konsultasi',
    statusCompleted: 'Sesi selesai',
    statusCompletedShort: 'Selesai',
    statusNoShow: 'Tidak hadir',
    statusCancelled: 'Dibatalkan',
    createTitle: 'Cipta sesi baharu',
    searchMrn: 'Cari mengikut MRN',
    searchMrnPlaceholder: 'Masukkan 4+ digit terakhir MRN untuk mencari',
    searching: 'Mencari...',
    patientName: 'Nama pesakit *',
    patientNamePlaceholder: 'Masukkan nama penuh pesakit',
    phone: 'Nombor telefon *',
    phonePlaceholder: 'Masukkan nombor telefon',
    mrnOptional: 'Nombor rekod perubatan (pilihan)',
    mrnPlaceholder: 'Masukkan MRN klinik jika ada',
    dob: 'Tarikh lahir *',
    dobRequired: 'Tarikh lahir diperlukan',
    dobMissingOnRecord:
      'Rekod pesakit ini tiada tarikh lahir. Tambah dahulu untuk membuat sesi.',
    patientMatchHint:
      'Taip sekurang-kurangnya dua huruf nama untuk melihat pesakit sedia ada.',
    matchingPatients: 'Pesakit sedia ada yang sepadan',
    sameNameWarning:
      'Semak: lebih daripada satu rekod berkongsi nama ini. Sahkan dengan digit telefon atau tarikh lahir sebelum memilih.',
    noneOfTheseNewPatient: 'Bukan mana-mana — teruskan sebagai pesakit baharu',
    sendLinkHint: 'Hantar pautan pra-konsultasi',
    resendLinkHint: 'Hantar pautan pra-konsultasi baharu',
    sendLinkTitle: 'Hantar pautan pra-konsultasi?',
    resendLinkTitle: 'Hantar pautan pra-konsultasi baharu?',
    resendLinkBody: 'Pautan pra-konsultasi akan dihantar melalui WhatsApp kepada {name} ({phone}).',
    resendLinkSupersedes: 'Pautan yang dihantar sebelum ini akan berhenti berfungsi sebaik sahaja pautan baharu dihantar.',
    resendLinkWindow: 'Pautan mengikut masa lawatan: dibuka dua hari sebelum temu janji dan berhenti berfungsi beberapa jam selepas ia bermula.',
    resendLinkConfirm: 'Hantar pautan',
    resendLinkSent: 'Pautan pra-konsultasi dihantar kepada {name}. Pautan terdahulu tidak lagi sah.',
    resendLinkAlreadyDone: 'Pautan tidak dihantar: pesakit ini sudah melengkapkan pra-konsultasi untuk sesi ini.',
    resendLinkFailed: 'Pautan pra-konsultasi tidak dapat dihantar.',
    assignDoctor: 'Tugaskan doktor *',
    selectDoctor: 'Pilih doktor',
    appointmentDate: 'Tarikh temujanji *',
    appointmentTime: 'Masa temujanji *',
    sessionType: 'Jenis sesi *',
    typePhysical: 'Bersemuka',
    typeOnline: 'Dalam talian',
    practitionerType: 'Jenis pengamal *',
    patientLanguage: 'Bahasa pesakit',
    rheumatologist: 'Pakar reumatologi',
    dermatologist: 'Pakar dermatologi',
    generalPractitioner: 'Doktor am',
    viewTitle: 'Lihat butiran sesi',
    viewConfirm: 'Lihat sesi',
    viewSubtitle: 'Anda akan melihat maklumat sesi',
    sessionInformation: 'Maklumat sesi',
    sessionId: 'ID sesi',
    patient: 'Pesakit',
    date: 'Tarikh',
    time: 'Masa',
    status: 'Status',
    editAppointment: 'Sunting temujanji',
    selectDateRange: 'Pilih julat tarikh',
    from: 'Dari:',
    to: 'Hingga:',
    bulkTitle: 'Muat naik temujanji pukal',
    csvRequirements: 'Keperluan fail CSV:',
    csvNoHeaders: 'Fail mesti dalam format CSV (.csv) tanpa pengepala',
    csvMaxSize: 'Saiz fail maksimum: 5MB',
    dropCsv: 'Klik untuk pilih fail CSV atau seret dan lepas',
    maxSizeHint: 'Saiz fail maksimum: 5MB',
    bulkSummary: 'Ringkasan muat naik pukal',
    totalProcessed: 'Jumlah diproses',
    successful: 'Berjaya',
    failed: 'Gagal',
    skipped: 'Dilangkau',
    ignored: 'Diabaikan',
    issues: 'Isu yang dijumpai:',
    downloadFailed: 'Muat turun CSV rekod gagal',
    calToday: 'Hari ini',
    patientUnavailable: 'Pesakit tidak tersedia',
    unassigned: 'Belum ditugaskan',
    notSet: 'Belum ditetapkan',
    sessionCalendar: 'Kalendar sesi',
    colAppointmentDate: 'Tarikh temujanji',
    colAppointmentTime: 'Masa temujanji',
    clickToView: 'Klik untuk melihat butiran sesi',
    editAppointmentHint: 'Sunting tarikh/masa temujanji',
    sessionCount: '{count} sesi',
    sessionCountPlural: '{count} sesi',
    weekdayMon: 'Isn',
    weekdayTue: 'Sel',
    weekdayWed: 'Rab',
    weekdayThu: 'Kha',
    weekdayFri: 'Jum',
    weekdaySat: 'Sab',
    weekdaySun: 'Ahd',
    loadFailed: 'Gagal memuatkan sesi. Sila cuba lagi.',
    navigateFailed: 'Gagal membuka sesi. Sila cuba lagi.',
    appointmentUpdated: 'Tarikh/masa temujanji dikemas kini untuk sesi #{id}',
    updateAppointmentFailed: 'Gagal mengemas kini temujanji. Sila cuba lagi.',
    dateRequired: 'Tarikh temujanji diperlukan',
    datePast: 'Tarikh temujanji tidak boleh pada masa lalu (masa Singapura)',
    timeRequired: 'Masa temujanji diperlukan',
    nameRequired: 'Nama pesakit diperlukan',
    nameInvalid: 'Sila masukkan nama yang sah (2-50 aksara)',
    phoneRequired: 'Nombor telefon diperlukan',
    phoneInvalid: 'Sila masukkan nombor telefon yang sah',
    doctorRequired: 'Sila pilih doktor untuk ditugaskan',
    sessionCreated: 'Sesi berjaya dicipta! ID sesi: {id}',
    createFailed: 'Gagal mencipta sesi. Sila cuba lagi.',
    statusUpdated: 'Status temujanji dikemas kini kepada "{status}"',
    statusUpdateFailed: 'Gagal mengemas kini status temujanji. Sila cuba lagi.',
    maxRows: 'Maksimum {max} baris setiap halaman.',
    selectValidCsv: 'Sila pilih fail CSV yang sah',
    fileTooLarge: 'Saiz fail mestilah kurang daripada 5MB',
    selectCsv: 'Sila pilih fail CSV',
    uploadFailed: 'Gagal memuat naik fail CSV. Sila cuba lagi.',
    dropValidCsv: 'Sila lepas fail CSV yang sah',
    csvColumnOrder: 'Susunan lajur wajib (15 lajur):',
    downloadSample: 'Muat turun sampel CSV',
    changeFile: 'Tukar fail',
    uploading: 'Memuat naik...',
    uploadCsv: 'Muat naik CSV',
    failedRecords: 'Rekod gagal ({count})',
    skippedRecords: 'Rekod dilangkau ({count})',
    ignoredRecords: 'Rekod diabaikan ({count})',
    rowLabel: 'Baris {row}',
    unknownError: 'Ralat tidak diketahui',
    unknownReason: 'Sebab tidak diketahui',
    moreFailed: '...dan {count} lagi rekod gagal',
    moreSkipped: '...dan {count} lagi rekod dilangkau',
    moreIgnored: '...dan {count} lagi rekod diabaikan',
  },
  consult: {
    readyTitle: 'Sedia untuk transkripsi',
    readyBody: 'Mulakan rakaman untuk menangkap konsultasi',
    consultLanguage: 'Bahasa konsultasi',
    transcriptionLanguage: 'Bahasa Konsultasi',
    outputLanguage: 'Bahasa Nota',
    outputLanguageHint: 'Nota dan analisis AI akan ditulis dalam bahasa nota',
    consultLanguageLocked: 'Tidak termasuk dalam pelan anda',
    startRecording: 'Mula rakaman',
    uploadWav: 'Muat naik WAV (Dev)',
    uploading: 'Memuat naik rakaman...',
    transcribing: 'Mentranskripsi audio...',
    analyzing: 'Menganalisis konsultasi...',
    complete: 'Selesai!',
    processing: 'Memproses...',
    uploadingHint: 'Menghantar audio ke pelayan',
    transcribingHint: 'Menukar pertuturan kepada teks',
    analyzingHint: 'Menjana pandangan klinikal',
    completeHint: 'Menyiapkan',
    processingHint: 'Sila tunggu',
    initializing: 'Memulakan...',
    initializingHint: 'Menyediakan sambungan audio',
    recording: 'Merakam',
    paused: 'Dijeda',
    resume: 'Sambung',
    pause: 'Jeda',
    stop: 'Berhenti',
    recordingPaused: 'Rakaman dijeda',
    recordingAudio: 'Merakam audio konsultasi...',
    stopWhenFinished: 'Klik Berhenti apabila selesai',
    stopped: 'Berhenti',
    startNewRecording: 'Mula rakaman baharu',
    recordingComplete: 'Rakaman selesai',
    insertHint: 'Klik "Masukkan ke nota" untuk transkripsi dan analisis',
    insertToNotes: 'Masukkan ke nota',
    consentTitle: 'Persetujuan pesakit diperlukan',
    consentBody:
      'Sebelum memulakan sesi rakaman untuk transkripsi perubatan, pastikan anda telah memperoleh persetujuan yang sesuai daripada pesakit.',
    consentCheckbox:
      'Saya mengesahkan bahawa saya telah memperoleh persetujuan yang sesuai daripada pesakit untuk rakaman audio dan transkripsi konsultasi perubatan ini.',
    acknowledge: 'Faham',
    startTitle: 'Mula rakaman',
    startBody: 'Adakah anda pasti mahu mula merakam? Ini akan menangkap audio daripada mikrofon anda untuk transkripsi.',
    yesStart: 'Ya, mula rakaman',
    privateNotes: 'Nota peribadi',
    saving: '(Menyimpan...)',
    privateNotesPlaceholder: 'Tambah nota peribadi tentang pesakit ini...',
    visitsTitle: 'Garis masa semua lawatan',
    loadingHistory: 'Memuatkan sejarah pesakit...',
    sessionNumber: 'Sesi #{id}',
    appointmentTime: 'Masa temujanji: {time}',
    noPreviousVisits: 'Tiada lawatan terdahulu untuk pesakit ini.',
    languageUpdateFailed: 'Gagal mengemas kini bahasa konsultasi',
    inConsultSaved: 'Data semasa konsultasi disimpan',
    inConsultSaveFailed: 'Gagal menyimpan data semasa konsultasi',
    signedOff: 'Sesi ditandatangani dan ditamatkan.',
    endFailed: 'Gagal menamatkan sesi. Sila cuba lagi.',
    noAudio: 'Tiada audio dirakam',
    analysisFailed: 'Analisis gagal selepas cubaan semula — sila cuba lagi',
    transcribed: 'Rakaman ditranskripsi dan dianalisis',
    processFailed: 'Gagal memproses rakaman',
    recovered: 'Rakaman dipulihkan ditranskripsi dan dianalisis',
    recoverFailed: 'Gagal memulihkan rakaman',
    wavDone: 'Fail WAV ditranskripsi dan dianalisis',
    wavFailed: 'Gagal memproses muat naik WAV',
    alreadySignedOff: 'Sesi sudah ditandatangani',
    alreadySignedOffBody:
      'Sesi ini sudah ditandatangani, jadi ubatnya telah diluluskan. Menjalankan semula jurutulis boleh menyemak atau menggandakan preskripsi diluluskan. Teruskan hanya jika anda ingin mengemas kini rekod konsultasi ini.',
    rerunScribe: 'Jalankan semula jurutulis',
    endTitle: 'Tamatkan sesi dan tandatangan?',
    endBody: 'Ini menyelesaikan konsultasi dan menghantar nota ke sistem klinik.',
    endConfirm: 'Tamatkan sesi',
    sectionLoading: 'Menganalisis...',
    sectionFailed: 'Analisis gagal, memaparkan nota sebelumnya',
    analyzingProgressive: 'Membina nota bahagian demi bahagian...',
    analyzingProgressiveHint: 'Setiap bahagian dipaparkan sebaik sahaja siap',
    runInProgress: 'Analisis jurutulis masih berjalan. Sila tunggu sehingga semua bahagian selesai.',
    scribeAgents: {
      history: 'Sejarah',
      examination: 'Pemeriksaan',
      diagnosis: 'Diagnosis',
      medication: 'Ubat-ubatan',
      eq: 'Peringatan hubungan',
      followup: 'Susulan',
    },
  },
  workspace: {
    tabs: {
      preClerk: 'Pra-Klerk',
      inConsult: 'Dalam Konsultasi',
      workflow: 'Aliran Kerja',
      sessionSummary: 'Ringkasan Sesi',
      chroniq: 'Chroniq',
      clinicalSummary: 'Ringkasan Klinikal',
      careGaps: 'Jurang penjagaan',
    },
    careGaps: {
      title: 'Jurang penjagaan',
      subtitle: 'Sasaran vs. aktiviti penyakit semasa, daripada lawatan yang telah disahkan',
      ruleset: 'Set peraturan {version}',
      openCount: '{count} terbuka',
      onTarget: 'Mencapai sasaran',
      reevaluate: 'Nilai semula',
      reevaluating: 'Menilai…',
      loading: 'Menyemak jurang penjagaan…',
      empty: 'Mencapai sasaran. Tiada jurang penjagaan terbuka.',
      silent: 'Enjin sedang menilai secara senyap. Jurang akan dipaparkan di sini setelah paparan diaktifkan untuk klinik ini.',
      showHistory: 'Tunjukkan {count} yang selesai atau ditutup',
      hideHistory: 'Sembunyikan yang selesai dan ditutup',
      recorded: 'Direkod',
      noScore: 'Tiada skor aktiviti direkod',
      targetNotMet: 'Sasaran tidak dicapai',
      targetMet: 'Sasaran dicapai',
      targetUnknown: 'Sasaran: tidak dapat ditentukan',
      action: 'Tindakan dicadangkan',
      due: 'Tarikh akhir {date}',
      closedOn: 'Ditutup {date}',
      close: 'Tutup jurang',
      exception: 'Tambah pengecualian',
      rule: {
        measurementMissing: 'Tiada skor aktiviti penyakit direkod',
        aboveTarget: 'Aktiviti penyakit melebihi sasaran',
      },
      disease: { ra: 'RA', psa: 'PsA', axspa: 'axSpA', sle: 'SLE' },
      status: {
        open: 'Terbuka',
        resolved: 'Selesai',
        excepted: 'Dikecualikan',
        closed: 'Ditutup',
        withdrawn: 'Ditarik balik',
      },
      dialog: {
        closeTitle: 'Tutup jurang penjagaan',
        closeHint: 'Rekodkan bukti bahawa kriteria penutupan telah dipenuhi.',
        evidenceLabel: 'Bukti penutupan',
        evidencePlaceholder: 'cth. CDAI 8 direkod hari ini; sasaran dicapai',
        confirmClose: 'Tutup jurang',
        exceptionTitle: 'Rekod pengecualian',
        exceptionHint: 'Sasaran individu atau alasan didokumenkan untuk menerima aktiviti semasa. Jurang kekal ditahan sehingga pengecualian tamat.',
        rationaleLabel: 'Rasional',
        rationalePlaceholder: 'cth. Kehamilan: treat-to-target ditangguh; semak pada minggu ke-20',
        expiresLabel: 'Semak sebelum',
        expiresHint: 'Mesti tarikh akan datang, paling lama 12 bulan.',
        confirmException: 'Simpan pengecualian',
        cancel: 'Batal',
        saving: 'Menyimpan…',
      },
      errors: {
        displayOff: 'Jurang penjagaan belum diaktifkan untuk klinik ini.',
        evidenceRequired: 'Bukti penutupan diperlukan.',
        rationaleRequired: 'Rasional diperlukan.',
        notFound: 'Jurang penjagaan ini tidak lagi terbuka.',
        invalidException: 'Pengecualian mesti tamat pada tarikh akan datang dalam tempoh 12 bulan.',
        generic: 'Tidak dapat mengemas kini jurang penjagaan. Sila cuba lagi.',
      },
    },
    subTabs: {
      history: 'Sejarah (Hx)',
      exam: 'Pemeriksaan',
      dx: 'Dx/Penilaian',
      plan: 'Rancangan / Mx',
      billing: 'Pengebilan & Pesanan',
      pro: 'PRO',
      aiScribe: 'AI Scribe',
    },
    headings: {
      practitionerNotes: 'Nota Pengamal',
      practitionerNotesEmpty: 'Tiada nota pengamal. Klik untuk menambah.',
      practitionerNotesPlaceholder: 'Klik untuk menambah nota...',
      hpi: 'Sejarah Penyakit Semasa',
      hpiPlaceholder: 'Masukkan sejarah penyakit semasa...',
      hpiEmpty: 'Tiada sejarah penyakit semasa. Klik untuk menambah.',
      ros: 'Tinjauan Sistem',
      rosPlaceholder: 'Masukkan tinjauan sistem (cth., Konstitusi: Menafikan demam...)',
      rosEmpty: 'Tiada tinjauan sistem. Klik untuk menambah.',
      pastMedicalHx: 'SEJARAH PERUBATAN LALU',
      pastMedicalHxPlaceholder: 'Masukkan sejarah perubatan lalu...',
      pastMedicalHxEmpty: 'Tiada sejarah perubatan lalu. Klik untuk menambah.',
      pastSurgicalHx: 'SEJARAH PEMBEDAHAN LALU',
      pastSurgicalHxPlaceholder: 'Masukkan sejarah pembedahan lalu...',
      pastSurgicalHxEmpty: 'Tiada sejarah pembedahan lalu. Klik untuk menambah.',
      familyHistory: 'SEJARAH KELUARGA',
      familyHistoryAdd: 'Tambah',
      familyHistoryAddTitle: 'Tambah ahli keluarga',
      familyHistoryEmpty: 'Tiada sejarah keluarga',
      familyHistoryEmptyHint: 'Mulakan dengan menambah pesakit (proband) ke pokok keluarga.',
      familyHistoryStart: 'Mulakan pokok keluarga',
      familyHistoryExpanded: 'Sejarah Keluarga - Paparan dikembangkan',
      familyHistoryMale: 'Lelaki',
      familyHistoryFemale: 'Perempuan',
      familyHistoryAffected: 'Terjejas',
      familyHistoryDeceased: 'Meninggal dunia',
      medsAllergies: 'UBAT & ALERGI',
      medications: 'Ubat:',
      medicationsPlaceholder: 'Masukkan ubat semasa...',
      medicationsEmpty: 'Tiada ubat direkodkan. Klik untuk menambah.',
      allergies: 'Alergi:',
      allergiesPlaceholder: 'Masukkan alergi...',
      allergiesEmpty: 'Tiada alergi direkodkan. Klik untuk menambah.',
      socialHistory: 'SEJARAH SOSIAL',
      socialHistoryPlaceholder: 'Masukkan sejarah sosial...',
      socialHistoryEmpty: 'Tiada sejarah sosial. Klik untuk menambah.',
      historySection: 'SEJARAH',
      rosPlaceholderBrief: 'Masukkan tinjauan sistem...',
      hpiEmptyBrief: 'Tiada sejarah penyakit semasa direkodkan.',
      rosEmptyBrief: 'Tiada tinjauan sistem direkodkan.',
      pastMedicalHxEmptyBrief: 'Tiada sejarah perubatan lalu.',
      pastSurgicalHxEmptyBrief: 'Tiada sejarah pembedahan lalu.',
      medicationsEmptyBrief: 'Tiada ubat direkodkan.',
      socialHistoryEmptyBrief: 'Tiada sejarah sosial direkodkan.',
      copyToClipboard: 'Salin ke papan keratan',
    },
    left: {
      mrn: 'MRN',
      noMrn: 'MRN tidak tersedia',
      noName: 'Nama tidak tersedia',
      ageGender: 'UMUR / JANTINA',
      lastVisit: 'LAWATAN TERAKHIR',
      noPreviousVisits: 'Tiada lawatan terdahulu',
      privateNote: 'NOTA PERIBADI',
      privateNotePlaceholder: 'Tambah konteks klinikal...',
      activeProblems: 'Masalah Aktif',
      noActiveProblems: 'Tiada masalah aktif',
      activeMedications: 'Ubat Aktif',
      noActiveMedications: 'Tiada ubat aktif',
      lastInvestigation: 'Siasatan Terakhir',
      noData: 'Tiada data',
      moreOptions: 'Lagi pilihan',
      edit: 'Sunting',
    },
    alerts: {
      title: 'Alahan & Amaran',
      drugAllergies: 'Alahan ubat',
      otherAlerts: 'Amaran lain',
      noAllergyRecord: 'Belum ada rekod alahan.',
      noKnownDrugAllergies: 'Tiada alahan ubat yang diketahui',
      addAllergy: 'Tambah alahan',
      addAlert: 'Tambah amaran',
      allergyPlaceholder: 'Ubat dan reaksi, cth. Penisilin - ruam',
      alertPlaceholder: 'Butiran amaran',
      medicalAlert: 'Amaran perubatan',
      anticoagulation: 'Antikoagulasi',
      fromChat: 'Daripada sembang pra-konsultasi',
      recordedByDoctor: 'Direkodkan oleh doktor',
      edit: 'Sunting',
      remove: 'Buang',
      confirmRemove: 'Buang entri ini?',
      loadFailed: 'Alahan dan amaran tidak dapat dimuatkan.',
      saveFailed: 'Tidak dapat menyimpan: {reason}',
    },
    right: {
      timeline: 'GARIS MASA',
      rx: 'RX',
      review: 'SEMAK',
      ix: 'IX',
      orders: 'PESANAN',
      quickRx: 'QUICK RX',
      letters: 'SURAT',
      visitTimeline: 'Garis masa lawatan',
      loadingHistory: 'Memuatkan sejarah pesakit...',
      noPreviousVisits: 'Tiada lawatan terdahulu.',
      sessionLabel: 'Sesi #{id} - {name}',
      patientFallback: 'Pesakit',
      currentlyViewing: 'Sedang dilihat',
      didNotAttend: 'Tidak hadir',
      completed: 'Selesai',
      closed: 'Ditutup',
      consultationComplete: 'Konsultasi selesai',
      inProgress: 'Sedang berlangsung',
    },
    preclerk: {
      patientHistory: 'Sejarah & Gejala Pesakit (Dijana AI)',
      noPatientHistory: 'Sejarah pesakit tidak tersedia',
      ros: 'Tinjauan Sistem:',
      noRos: 'Tinjauan sistem tidak tersedia',
      pastMedical: 'Kebimbangan Perubatan Lalu:',
      noPastMedical: 'Kebimbangan perubatan lalu tidak tersedia',
      pastSurgical: 'Kebimbangan Pembedahan Lalu:',
      noPastSurgical: 'Kebimbangan pembedahan lalu tidak tersedia',
      familyHistory: 'Sejarah Keluarga:',
      noFamilyHistory: 'Sejarah keluarga tidak tersedia',
      drugAllergies: 'Ubat & Alergi:',
      noDrugAllergies: 'Ubat atau alergi tidak tersedia',
      socialHistory: 'Sejarah Sosial:',
      noSocialHistory: 'Sejarah sosial tidak tersedia',
    },
    exam: {
      msk: 'MSK',
      general: 'Umum',
      skinHeent: 'Kulit/HEENT',
      eyes: 'Mata',
      cvs: 'Kardio',
      respiratory: 'Nafas',
      abdominal: 'Abdomen',
      neuro: 'Saraf',
      summary: 'Ringkasan',
      skinExam: 'Pemeriksaan kulit',
      generalAppearance: 'PENAMPILAN UMUM',
      skinMucocutaneous: 'KULIT & MUKOKUTAN',
      examinerSummary: 'RINGKASAN & TANGGAPAN PEMERIKSA',
      diseaseActivity: 'SKOR AKTIVITI PENYAKIT & PRO',
      cardiovascular: 'KARDIOVASKULAR',
      jointExam: 'MUSKULOSKELETAL / PEMERIKSAAN SENDI',
      neurological: 'NEUROLOGI',
      eyesSection: 'MATA',
      respiratorySection: 'RESPIRATORI',
      abdominalSection: 'ABDOMEN',
      lymphoreticular: 'LIMFORETIKULAR & SALIVA',
    },
    dx: {
      section: 'DX / PENILAIAN',
      primary: 'DIAGNOSIS UTAMA',
      principal: 'Masalah Utama',
      acute: 'Diagnosis Akut',
      chronic: 'Diagnosis Kronik',
      differential: 'Diagnosis Pembezaan yang Dipertimbangkan',
      clinicalReasoning: 'Penaakulan Klinikal',
      addDiagnosis: 'Tambah Diagnosis',
      addAcute: 'Tambah Diagnosis Akut',
      addChronic: 'Tambah Diagnosis Kronik',
      addDifferential: 'Tambah Diagnosis Pembezaan',
      noPrimary: 'Diagnosis utama tidak tersedia',
      noAcute: 'Tiada diagnosis akut',
      noChronic: 'Tiada diagnosis kronik',
      noDifferential: 'Tiada diagnosis pembezaan dipertimbangkan',
      insights: 'Wawasan Klinikal daripada Garis Panduan',
      insightsUpper: 'WAWASAN KLINIKAL DARIPADA GARIS PANDUAN',
    },
    ehrChat: {
      title: 'Sembang dengan EHR',
      ready: 'Pembantu AI sedia',
      unavailable: 'Tidak tersedia',
      initializing: 'Memulakan...',
      close: 'Tutup sembang',
      connecting: 'Menyambung ke EHR...',
      medicationHistory: 'Sejarah ubat',
      medicationHistoryQuery: 'Tunjukkan sejarah ubat',
      recentLabs: 'Makmal terkini',
      recentLabsQuery: 'Ringkaskan makmal terkini',
      previousVisits: 'Lawatan terdahulu',
      previousVisitsQuery: 'Tunjukkan lawatan terdahulu',
      placeholder: 'Tanya tentang data pesakit, ubat, keputusan...',
      send: 'Hantar mesej',
    },
  },
};
